import TransformComponent from 'Component/Transform';
import GameObject from 'GameObject/GameObject';
import { PlaySound } from 'System/Audio/AudioHelper';
import PIXIRenderer from 'System/Render/PIXIRenderer';
import { Container, interaction } from 'pixi.js';

const handleThreshold = 200;

export function createSettingVolumeSeekBar(
    initialValue: number,
    background: GameObject,
    handle: GameObject,
    fill: GameObject,
    onChanged: (value: number) => void
): SettingVolumeSeekBar {
    return new SettingVolumeSeekBar(initialValue, background, handle, fill, onChanged);
}

export default class SettingVolumeSeekBar {
    private fill: GameObject<Container>;
    private handle: GameObject<Container>;
    private background: GameObject<Container>;
    private value: number;
    private dragging: boolean;
    private handleTransform: TransformComponent;
    private fillTransform: TransformComponent;
    private startDragX: number;
    private handleStartPositionX: number;
    private initialValue: number;
    private onChanged: (value: number) => void;

    constructor(
        initialValue: number,
        background: GameObject,
        handle: GameObject,
        fill: GameObject,
        onChanged: (value: number) => void
    ) {
        this.handle = handle;
        this.fill = fill;
        this.background = background;

        this.initialValue = initialValue;
        this.onChanged = onChanged;

        this.handleTransform = this.handle.getComponent(TransformComponent);
        this.fillTransform = this.fill.getComponent(TransformComponent);

        this.handle.on('pointerdown', this.onHandleDown, this);
        this.handle.on('pointerup', this.onHandleUp, this);
        this.handle.on('pointerupoutside', this.onHandleUp, this);

        this.background.on('pointerup', this.onBackgroundUp, this);

        this.dragging = false;

        this.initialize();
    }

    private onHandleDown(event: interaction.InteractionEvent): void {
        this.dragging = true;
        this.startDragX = event.data.global.x;
        this.handleStartPositionX = this.handleTransform.x;
        PIXIRenderer.renderer.plugins.interaction.on('pointermove', this.onHandleMove, this);
    }

    private onHandleUp(_event: interaction.InteractionEvent): void {
        PlaySound('button-press');
        this.dragging = false;
        PIXIRenderer.renderer.plugins.interaction.off('pointermove', this.onHandleMove, this);
        this.onChanged(this.value);
    }

    private onHandleMove(event: interaction.InteractionEvent): void {
        if (!this.dragging) return;

        let newPositionX = event.data.global.x - this.startDragX;
        newPositionX = this.handleStartPositionX + newPositionX;
        const clampedPositionX = newPositionX < 0 ? 0 : newPositionX > handleThreshold ? handleThreshold : newPositionX;
        this.handleTransform.x = clampedPositionX;

        this.updateValue();
    }

    private onBackgroundUp(event: interaction.InteractionEvent): void {
        let newPositionX = event.data.global.x - this.handleTransform.container.getGlobalPosition().x;
        newPositionX = this.handleTransform.x + newPositionX;
        const clampedPositionX = newPositionX < 0 ? 0 : newPositionX > handleThreshold ? handleThreshold : newPositionX;
        this.handleTransform.x = clampedPositionX;

        PlaySound('button-press');

        this.updateValue();
        this.onChanged(this.value);
    }

    private initialize(): void {
        this.value = this.initialValue;
        this.handleTransform.position.set(handleThreshold * this.value, 0);
        this.updateValue();
    }

    private updateValue(): void {
        if (this.fillTransform && this.handleTransform) {
            const normalizedX = Math.abs(this.handleTransform.x / handleThreshold);
            this.value = parseFloat(normalizedX.toFixed(1));
            this.fillTransform.width = Math.abs(this.handleTransform.x);
        }
    }

    setValue(value: number): void {
        if (this.initialValue !== value) {
            this.initialValue = value;
            this.initialize();
        }
    }

    getValue(): number {
        return this.value;
    }
}
